// src/components/SediCliente.js
// ═══════════════════════════════════════════════════════════════════════════
// SEDI E SITI DEL CLIENTE (ottobre 2026) — regole in utils/sediCliente.js
// ═══════════════════════════════════════════════════════════════════════════
// • SediManager: elenco modificabile nella finestra del cliente.
// • SediScheda: riquadro nella scheda cliente, con le proposte dagli indirizzi già usati
//   (impianti, offerte, commesse) da aggiungere con conferma.
// • SelettoreSede: scelta della sede dentro un documento (commessa, offerta, rapportino,
//   sopralluogo, DDT, impianto…). Restituisce una COPIA della sede; si può creare una
//   sede nuova (salvata nell'anagrafica) o scrivere un indirizzo a mano.
// ═══════════════════════════════════════════════════════════════════════════
import CampiIndirizzo from "./CampiIndirizzo";
import React, { useEffect, useState, useRef } from "react";
import { collection, query, where, onSnapshot, getDocs, doc, updateDoc, arrayUnion, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useToast } from "../utils/Toast";
import { messaggioErrore } from "../utils/erroriUtente";
import { useAuth } from "../contexts/AuthContext";
import { TIPI_SEDE, SEDE_VUOTA, puoGestireSedi, nuovoIdSede, indirizzoCompleto, sediDelCliente, copiaSede, copiaLibera, descrizioneSede, linkMappa, linkTelefono, proposteSedi, sedeCorrispondente } from "../utils/sediCliente";

const inp = { width: "100%", padding: "6px 9px", border: "1px solid #e5e7eb", borderRadius: 7, fontSize: 12.5, fontFamily: "'Inter',sans-serif", outline: "none", boxSizing: "border-box", background: "#fff" };
const lbl = { fontSize: 10.5, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 2 };

// ── Campi di una sede ──────────────────────────────────────────────────────
function CampiSede({ sede, onChange, compatto = false }) {
  const c = (k, v) => onChange({ ...sede, [k]: v });
  const campo = (k, label, ph = "", span = 1) => (
    <div style={{ gridColumn: `span ${span}` }}>
      <label style={lbl}>{label}</label>
      <input style={inp} value={sede[k] || ""} placeholder={ph} onChange={e => c(k, e.target.value)} />
    </div>
  );
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0,1fr))", gap: 6 }}>
      {campo("nome", "Nome della sede", "es. Stabilimento di Bologna", 4)}
      <div style={{ gridColumn: "span 2" }}>
        <label style={lbl}>Tipo</label>
        <select style={inp} value={sede.tipo || "operativa"} onChange={e => c("tipo", e.target.value)}>
          {Object.values(TIPI_SEDE).map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </div>
      {/* indirizzo a pezzi, con il comune dall'elenco e il controllo del CAP (W438) */}
      <div style={{ gridColumn: "span 6" }}>
        <CampiIndirizzo obbligatorio={false} inp={inp} lbl={lbl}
          valore={{ via: sede.indirizzo || "", civico: sede.civico || "", cap: sede.cap || "", comune: sede.comune || "", provincia: sede.provincia || "" }}
          onChange={x => onChange({ ...sede, indirizzo: x.via, civico: x.civico, cap: x.cap, comune: x.comune, provincia: x.provincia })}/>
      </div>
      {!compatto && <>
        {campo("referente", "Referente in loco", "Nome e ruolo", 3)}
        {campo("telefono", "Telefono", "", 3)}
        {campo("orari", "Orari di accesso", "es. 7:30–17:00", 3)}
        {campo("accesso", "Accesso, chiavi, badge", "", 3)}
        {campo("note", "Note (consegne, parcheggio…)", "", 6)}
      </>}
    </div>
  );
}

// ── Finestra del cliente: elenco modificabile ──────────────────────────────
export function SediManager({ sedi = [], onChange }) {
  const auth = useAuth();
  const elenco = Array.isArray(sedi) ? sedi : [];
  if (!puoGestireSedi(auth)) return (
    <div style={{ gridColumn: "1/-1", marginTop: 6, background: "#f5f3ff", border: "1.5px solid #c4b5fd", borderRadius: 10, padding: "10px 12px" }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: "#5b21b6", marginBottom: 2 }}>📍 Sedi e siti del cliente</div>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8 }}>Stabilimenti, impianti, cantieri, abitazioni: dove si lavora per questo cliente. Si scelgono poi in commesse, offerte, rapportini, DDT e impianti.</div>
      {elenco.length === 0 ? <div style={{ fontSize: 11.5, color: "#9ca3af" }}>Nessuna sede registrata.</div>
        : elenco.map(s => <div key={s.id} style={{ fontSize: 12, color: "#374151", padding: "2px 0" }}>{s.nome || TIPI_SEDE[s.tipo]?.label} — {indirizzoCompleto(s)}</div>)}
      <div style={{ fontSize: 10.5, color: "#9ca3af", marginTop: 4 }}>Le sedi le aggiungono e modificano titolare, ufficio, amministrazione e project manager.</div>
    </div>
  );
  const agg = (id, s) => onChange(elenco.map(x => x.id === id ? s : x));
  return (
    <div style={{ gridColumn: "1/-1", marginTop: 6, background: "#f5f3ff", border: "1.5px solid #c4b5fd", borderRadius: 10, padding: "10px 12px" }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: "#5b21b6", marginBottom: 2 }}>📍 Sedi e siti del cliente</div>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8 }}>Stabilimenti, impianti, cantieri, abitazioni: dove si lavora per questo cliente. Si scelgono poi in commesse, offerte, rapportini, DDT e impianti.</div>
      {elenco.length === 0 && <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>Nessuna sede oltre all'indirizzo della sede legale.</div>}
      {elenco.map(s => (
        <div key={s.id} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 8, marginBottom: 6, background: s.attiva === false ? "#f9fafb" : "#fff", opacity: s.attiva === false ? .7 : 1 }}>
          <CampiSede sede={s} onChange={n => agg(s.id, n)} />
          <div style={{ display: "flex", gap: 10, marginTop: 6, fontSize: 11 }}>
            <label style={{ display: "flex", gap: 4, alignItems: "center", color: "#6b7280", cursor: "pointer" }}>
              <input type="checkbox" checked={s.attiva !== false} onChange={e => agg(s.id, { ...s, attiva: e.target.checked })} /> Attiva (si propone nei documenti)
            </label>
            <span style={{ flex: 1 }} />
            <button type="button" onClick={() => onChange(elenco.filter(x => x.id !== s.id))} style={{ border: "none", background: "none", color: "#dc2626", cursor: "pointer", fontSize: 11 }}>Togli</button>
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...elenco, { ...SEDE_VUOTA, id: nuovoIdSede() }])}
        style={{ fontSize: 13, fontWeight: 700, padding: "7px 14px", borderRadius: 8, border: "none", background: "#7c3aed", color: "#fff", cursor: "pointer" }}>+ Aggiungi sede</button>
      <div style={{ fontSize: 10.5, color: "#9ca3af", marginTop: 4 }}>Una sede tolta qui resta nei documenti già fatti; per non proporla più basta togliere la spunta «Attiva».</div>
    </div>
  );
}

// ── Scheda cliente: riquadro sedi + proposte ───────────────────────────────
export function SediScheda({ cliente, orgId, onModifica }) {
  const { addToast } = useToast();
  const puoModificare = puoGestireSedi(useAuth());
  const [fonti, setFonti] = useState([]);
  const [scelte, setScelte] = useState({});
  useEffect(() => {
    if (!cliente?.id || !orgId || !puoModificare) return;
    (async () => {
      try {
        const [imp, off, prj] = await Promise.all([
          getDocs(query(collection(db, "impianti"), where("organizationId", "==", orgId), where("clienteId", "==", cliente.id))).catch(() => ({ docs: [] })),
          getDocs(query(collection(db, "elaborazioniOfferte"), where("orgId", "==", orgId), where("clienteId", "==", cliente.id))).catch(() => ({ docs: [] })),
          getDocs(query(collection(db, "projects"), where("organizationId", "==", orgId), where("clienteId", "==", cliente.id))).catch(() => ({ docs: [] })),
        ]);
        const f = [];
        imp.docs.forEach(d => { const x = d.data(); f.push({ testo: x.indirizzo || x.ubicazione, nome: x.ubicazione && x.indirizzo ? x.ubicazione : (x.denominazione || ""), origine: "impianti" }); });
        off.docs.forEach(d => { const x = d.data(); if (!x.sede?.sedeId) f.push({ testo: x.sitoCliente, origine: "offerte" }); });
        prj.docs.forEach(d => { const x = d.data(); if (x.sede && !x.sede.sedeId) f.push({ testo: x.sede.indirizzo, origine: "commesse" }); });
        setFonti(f);
      } catch { setFonti([]); }
    })();
  }, [cliente?.id, orgId, puoModificare]);
  const sedi = sediDelCliente(cliente);
  const proposte = proposteSedi(cliente, fonti);
  const aggiungi = async () => {
    const nuove = proposte.filter((p, i) => scelte[i]).map(p => ({ ...SEDE_VUOTA, id: nuovoIdSede(), nome: p.nome || "", tipo: p.origini.includes("impianti") ? "impianto" : "operativa", indirizzo: p.indirizzo }));
    if (!nuove.length) return;
    try {
      await updateDoc(doc(db, "clienti", cliente.id), { sedi: arrayUnion(...nuove), updatedAt: serverTimestamp() });
      setScelte({}); addToast(`${nuove.length} sed${nuove.length === 1 ? "e aggiunta" : "i aggiunte"}: puoi completarle da «Modifica»`, "success");
    } catch (e) { addToast(messaggioErrore(e, "aggiungere le sedi"), "error"); }
  };
  return (
    <div style={{ background: "#fff", border: "2px solid #7c3aed", borderRadius: 12, padding: 16, marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#1e1b4b", flex: 1 }}>📍 Sedi e siti del cliente ({sedi.length})</div>
        {puoModificare && onModifica && (
          <button type="button" onClick={onModifica}
            style={{ fontSize: 12.5, fontWeight: 700, padding: "6px 12px", borderRadius: 8, border: "none", background: "#7c3aed", color: "#fff", cursor: "pointer" }}>+ Aggiungi o modifica sedi</button>
        )}
      </div>
      {sedi.length === 0 && <div style={{ fontSize: 12, color: "#9ca3af" }}>Nessuna sede registrata.{puoModificare ? " Aggiungile con il pulsante qui sopra, oppure direttamente da una commessa o da un documento." : ""}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 8 }}>
        {sedi.map(s => {
          const ind = indirizzoCompleto(s);
          return (
            <div key={s.id} style={{ border: "1px solid #f3f4f6", borderRadius: 9, padding: "8px 10px", background: "#fafafa" }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "#111827" }}>{s.nome || TIPI_SEDE[s.tipo]?.label}</div>
              <div style={{ fontSize: 10.5, color: "#7c3aed", fontWeight: 600 }}>{TIPI_SEDE[s.tipo]?.label}{s.virtuale ? " · indirizzo dell'anagrafica" : ""}</div>
              {ind && <div style={{ fontSize: 11.5, color: "#4b5563", marginTop: 2 }}>{ind}</div>}
              {(s.referente || s.telefono) && <div style={{ fontSize: 11, color: "#6b7280" }}>{s.referente}{s.telefono ? ` · ${s.telefono}` : ""}</div>}
              {s.accesso && <div style={{ fontSize: 11, color: "#6b7280" }}>Accesso: {s.accesso}</div>}
              <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                {linkMappa(ind) && <a href={linkMappa(ind)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: "#2563eb", fontWeight: 600 }}>📍 Mappa</a>}
                {linkTelefono(s.telefono) && <a href={linkTelefono(s.telefono)} style={{ fontSize: 11, color: "#2563eb", fontWeight: 600 }}>📞 Chiama</a>}
              </div>
            </div>
          );
        })}
      </div>
      {puoModificare && proposte.length > 0 && (
        <div style={{ marginTop: 12, background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 9, padding: "10px 12px" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#5b21b6", marginBottom: 6 }}>Indirizzi già usati per questo cliente, non ancora registrati come sedi:</div>
          {proposte.map((p, i) => (
            <label key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start", fontSize: 12, color: "#374151", padding: "2px 0", cursor: "pointer" }}>
              <input type="checkbox" checked={!!scelte[i]} onChange={e => setScelte(s => ({ ...s, [i]: e.target.checked }))} style={{ marginTop: 2 }} />
              <span>{p.nome ? <b>{p.nome} — </b> : null}{p.indirizzo} <span style={{ color: "#9ca3af" }}>({p.origini.join(", ")})</span></span>
            </label>
          ))}
          <button type="button" onClick={aggiungi} disabled={!Object.values(scelte).some(Boolean)}
            style={{ marginTop: 6, fontSize: 12, fontWeight: 700, padding: "5px 12px", borderRadius: 7, border: "none", background: Object.values(scelte).some(Boolean) ? "#7c3aed" : "#d1d5db", color: "#fff", cursor: "pointer" }}>Aggiungi come sedi</button>
        </div>
      )}
    </div>
  );
}

// ── Scelta della sede in un documento ──────────────────────────────────────
/**
 * value: copia della sede già scelta (o null). onChange(copia | null).
 * proposta: copia da proporre se value è vuoto (es. la sede della commessa) — la applica da sé.
 */
export function SelettoreSede({ clienteId, value, onChange, etichetta = "Sede / luogo", proposta = null, disabled = false, consentiNuova: consentiNuovaProp = true }) {
  const { addToast } = useToast();
  // Nuove sedi nell'anagrafica: solo titolare, ufficio, amministrazione, PM (gli altri possono
  // comunque scegliere una sede esistente o scrivere un indirizzo a mano)
  // useAuth sempre chiamato (regola degli hook: mai dentro una condizione)
  const auth = useAuth();
  const consentiNuova = consentiNuovaProp && puoGestireSedi(auth);
  const [cliente, setCliente] = useState(null);
  const [modo, setModo] = useState(null); // null | "nuova" | "libero"
  const [nuova, setNuova] = useState({ ...SEDE_VUOTA });
  const [libero, setLibero] = useState("");
  useEffect(() => {
    if (!clienteId) { setCliente(null); return; }
    return onSnapshot(doc(db, "clienti", clienteId), s => setCliente(s.exists() ? { id: s.id, ...s.data() } : null), () => setCliente(null));
  }, [clienteId]);
  // La proposta (es. la sede della commessa) si applica solo se l'utente non ha ancora
  // scelto nulla: se ha svuotato o cambiato la sede a mano, non gliela si rimette.
  const toccato = useRef(false);
  const cambia = (v) => { toccato.current = true; onChange(v); };
  // Cambiando cliente la sede scelta (che era del cliente precedente) non vale più: si svuota.
  const clientePrec = useRef(clienteId);
  useEffect(() => {
    const prima = clientePrec.current;
    clientePrec.current = clienteId;
    if (prima === clienteId || disabled || !value) return;
    if (prima) { toccato.current = false; onChange(null); } // da "nessun cliente" a un cliente: è il caricamento del documento
  }, [clienteId]);
  useEffect(() => { if (!toccato.current && !value && proposta && (proposta.indirizzo || proposta.nome)) onChange(proposta); },
    // la proposta si applica una volta sola, quando arriva
    [proposta?.sedeId, proposta?.indirizzo]);
  const sedi = sediDelCliente(cliente);
  // La sede salvata nel documento si riconosce per id oppure, se nel frattempo è cambiata
  // (es. la "sede legale" ricavata dal vecchio indirizzo e poi registrata), per indirizzo.
  const corrisp = sedeCorrispondente(sedi, value);
  const sel = corrisp ? corrisp.id : value?.sedeId ? value.sedeId : (value?.indirizzo ? "__libero" : "");
  const salvaNuova = async () => {
    if (!nuova.indirizzo.trim() && !nuova.nome.trim()) { addToast("Scrivi almeno nome o indirizzo della sede", "error"); return; }
    const s = { ...nuova, id: nuovoIdSede() };
    try {
      if (clienteId) await updateDoc(doc(db, "clienti", clienteId), { sedi: arrayUnion(s), updatedAt: serverTimestamp() });
      cambia(copiaSede(s)); setModo(null); setNuova({ ...SEDE_VUOTA });
      if (clienteId) addToast("Sede aggiunta all'anagrafica del cliente", "success");
    } catch (e) { addToast(messaggioErrore(e, "salvare la sede"), "error"); }
  };
  const ind = value?.indirizzo;
  return (
    <div>
      {etichetta && <label style={{ ...lbl, fontSize: 11, color: "#374151", fontWeight: 700 }}>{etichetta}</label>}
      <div style={{ display: "flex", gap: 6 }}>
        <select style={{ ...inp, flex: 1 }} disabled={disabled} value={modo ? "__" + modo : sel}
          onChange={e => {
            const v = e.target.value;
            if (v === "__nuova") { setModo("nuova"); return; }
            if (v === "__libero") { setModo("libero"); setLibero(value && !value.sedeId ? value.indirizzo : ""); return; }
            setModo(null);
            if (!v) { cambia(null); return; }
            if (corrisp && v === corrisp.id && value) return; // stessa sede: la copia nel documento resta com'era
            const scelta = sedi.find(s => s.id === v);
            if (scelta) cambia(copiaSede(scelta));
          }}>
          <option value="">— {clienteId ? "Scegli la sede del cliente" : "Nessuna"} —</option>
          {sedi.map(s => <option key={s.id} value={s.id}>{descrizioneSede(copiaSede(s))}</option>)}
          {value?.sedeId && !corrisp && <option value={value.sedeId}>{descrizioneSede(value)} (non più attiva)</option>}
          {consentiNuova && clienteId && <option value="__nuova">+ Nuova sede del cliente…</option>}
          <option value="__libero">{value && !value.sedeId && value.indirizzo ? `Scritto a mano: ${value.indirizzo}` : "Altro indirizzo (scritto a mano)"}</option>
        </select>
        {linkMappa(ind) && !modo && <a href={linkMappa(ind)} target="_blank" rel="noopener noreferrer" title="Apri in Maps" style={{ alignSelf: "center", fontSize: 15, textDecoration: "none" }}>📍</a>}
      </div>
      {!modo && value && (value.referente || value.accesso || value.orari) && (
        <div style={{ fontSize: 10.5, color: "#6b7280", marginTop: 3 }}>
          {[value.referente && `Referente: ${value.referente}${value.telefono ? ` (${value.telefono})` : ""}`, value.orari && `Orari: ${value.orari}`, value.accesso && `Accesso: ${value.accesso}`].filter(Boolean).join(" · ")}
        </div>
      )}
      {modo === "nuova" && (
        <div style={{ marginTop: 6, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: 8 }}>
          <CampiSede sede={nuova} onChange={setNuova} compatto />
          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
            <button type="button" onClick={salvaNuova} style={{ fontSize: 12, fontWeight: 700, padding: "5px 12px", borderRadius: 7, border: "none", background: "#7c3aed", color: "#fff", cursor: "pointer" }}>Salva sede</button>
            <button type="button" onClick={() => setModo(null)} style={{ fontSize: 12, padding: "5px 10px", borderRadius: 7, border: "1px solid #e5e7eb", background: "#fff", cursor: "pointer" }}>Annulla</button>
            <span style={{ fontSize: 10.5, color: "#9ca3af", alignSelf: "center" }}>Referente e accessi si completano dall'anagrafica.</span>
          </div>
        </div>
      )}
      {modo === "libero" && (
        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <input style={{ ...inp, flex: 1 }} autoFocus value={libero} onChange={e => setLibero(e.target.value)} placeholder="Indirizzo (non viene salvato tra le sedi del cliente)" />
          <button type="button" onClick={() => { cambia(libero.trim() ? copiaLibera(libero) : null); setModo(null); }} style={{ fontSize: 12, fontWeight: 700, padding: "5px 12px", borderRadius: 7, border: "none", background: "#7c3aed", color: "#fff", cursor: "pointer" }}>OK</button>
        </div>
      )}
    </div>
  );
}

/** Riga di sola lettura: nome, indirizzo e link alla mappa. */
export function RigaSede({ sede, piccola = false }) {
  if (!sede || !(sede.indirizzo || sede.nome)) return null;
  const m = linkMappa(sede.indirizzo);
  return (
    <span style={{ fontSize: piccola ? 11 : 12, color: "#4b5563" }}>
      📍 {descrizioneSede(sede)}
      {m && <> · <a href={m} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb", fontWeight: 600 }}>Mappa</a></>}
    </span>
  );
}
